import {Injectable} from '@angular/core';
import {ParticipantModel, ParticipantRoleEnum} from '../models/partipant.model';
import {BehaviorSubject, Observable} from 'rxjs';

import {W3StorageService} from '@rapi/w3/apps/storage';
import {GraphChangedData, SessionModel, SessionPanel, UserSocketModel} from '@sip/common/models';
import {SipSocketService, SwInChannel} from '@sip/common/web-socket/sip-socket.service';

import {AlertAllParticipants, EventPayload, MessageRegistered, PostChanged, ProjectChangedConfig} from '../events';
import {ClearDataEvent} from './events';

@Injectable({
    providedIn: 'root'
})
export class SessionConfigService {

    public channel: SwInChannel<UserSocketModel>;
    public info = {
        view: null
    };
    public participant$: Observable<ParticipantModel>;
    public session$: Observable<SessionModel>;
    public broadCast$: Observable<EventPayload>;

    private participantSubject = new BehaviorSubject<ParticipantModel>(null);
    private sessionSubject = new BehaviorSubject<SessionModel>(null);
    private broadCastSubject = new BehaviorSubject<EventPayload>(null);

    // private _config: ConfigSessionModel = {
    //
    //     ///
    //     type: 'privada', // privada publica restrita
    //     enable_forward: true,
    //     apresentation_type: 'keypad', // keypag slide
    //     enable_change_view: false,
    //     enable_countdown: true,
    //     has_feedback: true
    // };

    constructor(private socket: SipSocketService, private storage: W3StorageService) {
        this.participant$ = this.participantSubject.asObservable();
        this.session$ = this.sessionSubject.asObservable();
        this.broadCast$ = this.broadCastSubject.asObservable();
    }

    current(): SessionModel {
        return this.sessionSubject.getValue();
    }

    participant(): ParticipantModel {
        return this.participantSubject.getValue();
    }

    // Modo representativo, com opção avançada de selecionar uma ou mais unidades por vez (contrario ao modo simples
    isRepresentativePro(): boolean {
        const p = this.participant();
        const s = this.current();
        return p && p.role === ParticipantRoleEnum.REPRESENTATIVE && !s.simple_representative;
    }

    // Modo representativo, permitindo apenas enviar voto para todas as unidades de uma vez
    isRepresentativeSimple(): boolean {
        const p = this.participant();
        const s = this.current();
        return p && p.role === ParticipantRoleEnum.REPRESENTATIVE && s.simple_representative;
    }

    private disableSocket(): void {
        this.channel = null;
        this.socket.reset();
    }

    private configureSocket(): void {

        this.disableSocket();

        const sessionId = this.current().id;
        const participantId = this.participant().id;
        const nameChSession = `vis.session.${sessionId}`;
        const nameChParticipant = `vis.participant.${participantId}`;

        this.socket.connect();
        this.channel = this.socket.joinChannel<UserSocketModel>(nameChSession);

        this.channel
            .listen('AlertAllParticipants', (e) => {
                this.broadCastSubject.next(new AlertAllParticipants(e.payload));
            });

        this.channel
            .listen('ProjectChangedConfig', (e) => {
                this.broadCastSubject.next(new ProjectChangedConfig(e.payload));
            });

        this.channel
            .listen('SessionPanel', (e) => {
                this.broadCastSubject.next(new SessionPanel(e));
            });

        this.channel
            .listen('GraphChangedData', (e) => {
                this.broadCastSubject.next(new GraphChangedData(e));
            });

        // Página (post) alterada no painel — usa broadcastAs('post.changed'),
        // por isso o nome do evento no Echo leva o prefixo ".".
        this.channel
            .listen('.post.changed', (e) => {
                this.broadCastSubject.next(new PostChanged(e.payload));
            });

        this.socket
            .createPrivateChannel(nameChParticipant)
            .listen('MessageRegistered', (e) => {
                this.broadCastSubject.next(new MessageRegistered(e.data.message, e.data.action));
            });
    }

    dispatchClearData(): void {
        this.broadCastSubject.next(new ClearDataEvent());
    }

    hasOtherSeason(code: string, id?: number): boolean {
        const currentId = +this.storage.get('session_id');
        const currentCode = this.storage.get('session_code');
        const checkId = +id;

        if (currentId > 0 && checkId > 0 && currentId !== checkId) {
            return true;
        }

        return currentCode && currentCode !== code;
    }

    activate(session: SessionModel): void {
        this.storage.set('session_id', session.id);
        this.storage.set('session_code', session.code);
        this.sessionSubject.next(session);
    }

    setParticipant(participant: ParticipantModel): void {
        if (participant) {
            this.checkSessionZoom();
        }

        this.participantSubject.next(participant);
        this.configureSocket();
    }

    isPrivate(): boolean {
        return this.identify === 'private';
    }

    isPublic(): boolean {
        return this.identify === 'public';
    }

    isAnonymous(): boolean {
        return this.identify === 'anonymous';
    }

    isRestrict(): boolean {
        return this.identify === 'restricted';
    }

    get identify(): string {
        return this.current() ? this.current().identify : 'restricted';
    }

    get type(): string {
        return this.current() ? this.current().type : 'poll';
    }

    get isPoll(): boolean {
        return this.type === 'poll';
    }

    get code(): string {
        return this.current() ? this.current().code : '';
    }

    get name(): string {
        return this.current() ? this.current().name : '';
    }

    get msgOrg(): string {
        if (this.current() && this.current().metas) {
            return this.current().metas.msg_inicio;
        }
    }

    get msgWait(): string {
        if (this.current() && this.current().metas) {
            return this.current().metas.msg_aguarde;
        }
    }

    // ----- Configurações do modo Prova (assíncrono / type=test) -----
    private get metas() {
        const s = this.current();
        return s && s.metas ? s.metas : null;
    }

    // Coerção defensiva: o backend pode enviar bool nativo, 1/0 ou "1"/"true".
    private metaBool(value: any, def: boolean): boolean {
        if (value === undefined || value === null || value === '') {
            return def;
        }
        return value === true || value === 1 || value === '1' || value === 'true';
    }

    // 2.2 - exibe o botão de FIM no app (default: true)
    get showFinishButton(): boolean {
        return this.metaBool(this.metas ? this.metas.test_show_finish_button : undefined, true);
    }

    // 2.2 - texto customizável do botão de FIM (default: "FIM")
    get finishButtonText(): string {
        return this.metas && this.metas.test_finish_button_text ? this.metas.test_finish_button_text : 'FIM';
    }

    // 2.3 - ao clicar em FIM vai para a página de resumo (gabarito) (default: false)
    get showSummaryPage(): boolean {
        return this.metaBool(this.metas ? this.metas.test_show_summary_page : undefined, false);
    }

    // 2.4 - ao concluir, exibe página de conclusão em vez de logout (default: false)
    get showConclusionPage(): boolean {
        return this.metaBool(this.metas ? this.metas.test_show_conclusion_page : undefined, false);
    }

    // 2.4 - id da página de conclusão a exibir ao finalizar (modo prova)
    get conclusionPageId(): number {
        return this.metas ? this.metas.test_conclusion_page_id : undefined;
    }

    // 2.5 - exibe contador de respostas no canto inferior direito (default: false)
    get showAnswersCounter(): boolean {
        return this.metaBool(this.metas ? this.metas.test_show_answers_counter : undefined, false);
    }

    // 2.6 - modo prova com retrocesso: permite refazer a prova e reenviar votos
    // mesmo após finalizar. Espelha o test_regress_enable do backend, que libera
    // a votação de um participante já finalizado (default: false).
    get allowRetake(): boolean {
        return this.metaBool(this.metas ? this.metas.test_regress_enable : undefined, false);
    }

    get isOpen(): boolean {
        return this.current() ? this.current().status : false;
    }

    get typeView(): string {
        return this.current() ? this.current().view : null;
    }

    openSession(): void {
        const session = this.current();
        session.status = true;
        this.activate(session);
    }

    // Fecha a sessão localmente (status=false) mantendo-a ativa no estado, sem
    // reset()/logout — usado no poll para o participante voltar à tela de aguarde
    // e poder reabrir quando o admin iniciar de novo.
    closeSession(): void {
        const session = this.current();
        if (!session) {
            return;
        }
        session.status = false;
        this.activate(session);
    }

    // se possui um USER ATIVO
    check(): boolean {
        return !!this.participant();
    }

    // Convidado ( não logado )
    guest(): boolean {
        return !this.check();
    }

    reset(): void {
        this.disableSocket();

        this.participantSubject.next(null);
        // this.participantSubject.complete();

        this.sessionSubject.next(null);
        // this.sessionSubject.complete();

        this.broadCastSubject.next(null);
        // this.broadCastSubject.complete();

        this.storage.remove('session_id');
        this.storage.remove('session_code');
    }

    private checkSessionZoom(): void {
        const session = this.current();

        // check is session with zoom AND Check if a Page Is Not in an iFrame
        if (session.zoom_enable && window.location === window.parent.location) {
            window.location.replace(`${window.location.origin}/zoom/`);
        }
    }

}
